import React, { useState, useEffect, useMemo } from 'react';
import { Contract, ContractCreate, PricingHeader, Customer, Company } from '../types';
import { BILLING_CYCLE_OPTIONS, CONTRACT_LENGTH_OPTIONS, NOTICE_PERIOD_OPTIONS, ContractBillingCycleValue, ContractLengthValueRestricted, NoticePeriodValue } from '../constants/billing';
import { ensureValidContractBillingCycle, ensureValidContractLengthRestricted, ensureValidNoticePeriod } from '../utils/billing-mappings';
import ContractPeriodSelector from './ContractPeriodSelector';
import { apiService } from '../services/api';

interface ContractFormProps {
  initialData?: Contract | null;
  pricingHeaders: PricingHeader[];
  customers: Customer[];
  preselectedCustomerId?: number;
  onSubmit: (data: ContractCreate) => void;
  onCancel: () => void;
}

const ContractForm: React.FC<ContractFormProps> = ({
  initialData,
  pricingHeaders,
  customers,
  preselectedCustomerId,
  onSubmit,
  onCancel
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Debug logging
  console.log('ContractForm props:', {
    pricingHeaders: pricingHeaders?.length || 0,
    customers: customers?.length || 0,
    initialData: initialData?.contract_name || 'new'
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await apiService.getCompanies();
      setCompanies(response);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };
  // If we have a preselected customer or a single customer and no initialData, pre-populate with that customer
  const preselected = !initialData && preselectedCustomerId ? customers.find((c: Customer) => c.id === preselectedCustomerId) : undefined;
  const defaultCustomerId = !initialData
    ? (preselected ? preselected.id : (customers.length === 1 ? customers[0].id : undefined))
    : undefined;
  const defaultContractName = !initialData
    ? (preselected ? `Contract for ${preselected.customer_name}` : (customers.length === 1 ? `Contract for ${customers[0].customer_name}` : ''))
    : '';
  const defaultCurrency = !initialData
    ? (preselected && ['USD', 'EUR', 'GBP'].includes(preselected.currency)
      ? preselected.currency as 'USD' | 'EUR' | 'GBP'
      : (customers.length === 1 && ['USD', 'EUR', 'GBP'].includes(customers[0].currency)
        ? customers[0].currency as 'USD' | 'EUR' | 'GBP'
        : 'EUR'))
    : 'EUR';

  const [formData, setFormData] = useState<ContractCreate>({
    contract_name: initialData?.contract_name || defaultContractName,
    customer_id: initialData?.customer_id || defaultCustomerId || undefined,
    fixed_pricing_header_id: initialData?.fixed_pricing_header_id || 0,
    company_id: initialData?.company_id || undefined,
    is_current_pricing_header: initialData?.is_current_pricing_header !== undefined ? initialData.is_current_pricing_header : true,
    start_date: initialData?.start_date 
      ? new Date(initialData.start_date).toISOString().split('T')[0]
      : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    end_date: initialData?.end_date 
      ? new Date(initialData.end_date).toISOString().split('T')[0]
      : '',
    billing_cycle: ensureValidContractBillingCycle(initialData?.billing_cycle || '1_month') as ContractBillingCycleValue,
    contract_length: ensureValidContractLengthRestricted(initialData?.contract_length || '12_months') as ContractLengthValueRestricted,
    notice_period: ensureValidNoticePeriod(initialData?.notice_period || '1_month') as NoticePeriodValue,
    currency: initialData?.currency || defaultCurrency,
    language: initialData?.language || 'en',
    payment_method: initialData?.payment_method || 'bank_transfer',
    due_day_of_month: initialData?.due_day_of_month || undefined,
    contract_createdby: initialData?.contract_createdby || 'current_user',
    isactive: initialData?.isactive !== undefined ? initialData.isactive : true,
    isdeleted: initialData?.isdeleted !== undefined ? initialData.isdeleted : false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Customer search support
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => (
      String(c.id).includes(q) ||
      (c.customer_name && c.customer_name.toLowerCase().includes(q)) ||
      ((c as any).customer_email_address && (c as any).customer_email_address.toLowerCase().includes(q)) ||
      (c.customer_type && c.customer_type.toLowerCase().includes(q))
    ));
  }, [customers, customerSearch]);
  
  // Track additional fields that are not part of ContractCreate
  const [contractUpdatedBy, setContractUpdatedBy] = useState<string>(initialData ? 'current_user' : '');
  const [isCurrent, setIsCurrent] = useState<boolean>(initialData?.iscurrent !== undefined ? initialData.iscurrent : true);

  // Use restricted options from constants
  const billingCycles = BILLING_CYCLE_OPTIONS;
  const contractLengths = CONTRACT_LENGTH_OPTIONS;
  const noticePeriods = NOTICE_PERIOD_OPTIONS;
  const currencies = ['EUR', 'USD', 'GBP', 'BRL', 'CAD', 'CHF'];
  const languages = [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'German' },
    { value: 'fr', label: 'French' },
    { value: 'es', label: 'Spanish' },
    { value: 'es-ch', label: 'Spanish (Switzerland)' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'pt-br', label: 'Portuguese (Brazil)' }
  ];
  const paymentMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'sepa_direct_debit', label: 'SEPA Direct Debit' },
    { value: 'boleto', label: 'Boleto' },
    { value: 'pix', label: 'PIX' }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let processedValue: any = value;
    
    // Process the value based on the field name
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (name.includes('_id')) {
      processedValue = value === '0' ? undefined : Number(value);
    } else if (name === 'billing_cycle') {
      processedValue = ensureValidContractBillingCycle(value) as ContractBillingCycleValue;
    } else if (name === 'contract_length') {
      processedValue = ensureValidContractLengthRestricted(value) as ContractLengthValueRestricted;
    } else if (name === 'notice_period') {
      processedValue = ensureValidNoticePeriod(value) as NoticePeriodValue;
    } else if (name === 'due_day_of_month') {
      processedValue = value === '' ? undefined : Number(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.contract_name.trim()) {
      newErrors.contract_name = 'Contract name is required';
    }



    if (!formData.fixed_pricing_header_id) {
      newErrors.fixed_pricing_header_id = 'Pricing header is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (formData.end_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = 'End date must be after start date';
    }

    if (!formData.contract_createdby.trim()) {
      newErrors.contract_createdby = 'Created by is required';
    }

    // Validate due_day_of_month if provided
    if (formData.due_day_of_month !== undefined && formData.due_day_of_month !== null) {
      if (formData.due_day_of_month < 1 || formData.due_day_of_month > 31) {
        newErrors.due_day_of_month = 'Due day of month must be between 1 and 31';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Convert dates to ISO format for the backend and ensure enum values are valid
      const submitData: any = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : '',
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : undefined,
        // Ensure enum values are in the correct format
        billing_cycle: formData.billing_cycle ? ensureValidContractBillingCycle(formData.billing_cycle) as ContractBillingCycleValue : '1_month' as ContractBillingCycleValue,
        contract_length: formData.contract_length ? ensureValidContractLengthRestricted(formData.contract_length) as ContractLengthValueRestricted : '12_months' as ContractLengthValueRestricted,
        notice_period: formData.notice_period ? ensureValidNoticePeriod(formData.notice_period) as NoticePeriodValue : '1_month' as NoticePeriodValue,
        // Handle company_id properly - send undefined if not selected
        company_id: formData.company_id && formData.company_id !== 0 ? formData.company_id : undefined,
        // Handle customer_id properly - send undefined if not selected
        customer_id: formData.customer_id && formData.customer_id !== 0 ? formData.customer_id : undefined
      };

      // When editing an existing contract include additional audit / status fields
      if (initialData) {
        submitData.contract_updatedby = contractUpdatedBy || 'current_user';
        submitData.iscurrent = isCurrent;
      }
      
      // Remove empty end_date if not provided
      if (!submitData.end_date) {
        delete submitData.end_date;
      }
      
      // Remove company_id if not provided
      if (!submitData.company_id) {
        delete submitData.company_id;
      }
      
      // Remove customer_id if not provided
      if (!submitData.customer_id) {
        delete submitData.customer_id;
      }
      
      // Remove due_day_of_month if not provided
      if (submitData.due_day_of_month === undefined || submitData.due_day_of_month === null) {
        delete submitData.due_day_of_month;
      }
      
      console.log('ContractForm - Submitting data:', submitData);
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Contract Name *</label>
        <input
          type="text"
          name="contract_name"
          value={formData.contract_name}
          onChange={handleChange}
          className={`form-control ${errors.contract_name ? 'error' : ''}`}
          placeholder="Enter contract name"
        />
        {errors.contract_name && <div className="form-error">{errors.contract_name}</div>}
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Customer</label>
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Search customer by name, email, type or ID..."
              className="form-control"
              style={{ marginBottom: '8px' }}
            />
            <select
              name="customer_id"
              value={formData.customer_id || 0}
              onChange={handleChange}
              className={`form-control ${errors.customer_id ? 'error' : ''}`}
            >
              <option value={0}>Select a customer (optional)</option>
              {filteredCustomers && filteredCustomers.length > 0 ? (
                filteredCustomers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customer_name} ({customer.customer_type})
                  </option>
                ))
              ) : (
                <option disabled>No customers available</option>
              )}
              {formData.customer_id !== 0 && !filteredCustomers.some((c: Customer) => c.id === formData.customer_id) && (
                (() => {
                  const selected = customers.find((c: Customer) => c.id === formData.customer_id);
                  return selected ? (
                    <option key="selected-customer" value={selected.id}>
                      {selected.customer_name} ({selected.customer_type})
                    </option>
                  ) : null;
                })()
              )}
            </select>
            {errors.customer_id && <div className="form-error">{errors.customer_id}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Pricing Header *</label>
            <select
              name="fixed_pricing_header_id"
              value={formData.fixed_pricing_header_id}
              onChange={handleChange}
              className={`form-control ${errors.fixed_pricing_header_id ? 'error' : ''}`}
            >
              <option value={0}>Select a pricing header</option>
              {pricingHeaders && pricingHeaders.length > 0 ? (
                pricingHeaders.filter(h => h.is_active).map((header) => (
                  <option key={header.pricing_header_id} value={header.pricing_header_id}>
                    {header.name} {header.category ? `(${header.category})` : ''}
                  </option>
                ))
              ) : (
                <option disabled>No pricing headers available</option>
              )}
            </select>
            {errors.fixed_pricing_header_id && <div className="form-error">{errors.fixed_pricing_header_id}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Invoicing Company</label>
            <select
              name="company_id"
              value={formData.company_id || 0}
              onChange={handleChange}
              className="form-control"
              disabled={loadingCompanies}
            >
              <option value={0}>Select a company (optional)</option>
              {!loadingCompanies && companies.length > 0 ? (
                companies.filter((c: Company) => c.is_active).map((company: Company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))
              ) : (
                <option disabled>
                  {loadingCompanies ? 'Loading companies...' : 'No companies available'}
                </option>
              )}
            </select>
          </div>
        </div>
        <div className="form-col">
          {/* Empty column for layout balance */}
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Start Date *</label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className={`form-control ${errors.start_date ? 'error' : ''}`}
            />
            {errors.start_date && <div className="form-error">{errors.start_date}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className={`form-control ${errors.end_date ? 'error' : ''}`}
            />
            {errors.end_date && <div className="form-error">{errors.end_date}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Billing Cycle</label>
            <select
              name="billing_cycle"
              value={formData.billing_cycle}
              onChange={handleChange}
              className="form-control"
            >
              {billingCycles.map((cycle) => (
                <option key={cycle.value} value={cycle.value}>
                  {cycle.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Contract Length</label>
            <ContractPeriodSelector
              value={formData.contract_length || '12_months'}
              onChange={(value) => {
                setFormData(prev => ({
                  ...prev,
                  contract_length: value
                }));
                // Clear error when user selects a value
                if (errors.contract_length) {
                  setErrors(prev => ({ ...prev, contract_length: '' }));
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Notice Period</label>
            <select
              name="notice_period"
              value={formData.notice_period}
              onChange={handleChange}
              className="form-control"
            >
              {noticePeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Currency</label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              className="form-control"
            >
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              className="form-control"
            >
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Due Day of Month</label>
            <input
              type="number"
              name="due_day_of_month"
              value={formData.due_day_of_month || ''}
              onChange={handleChange}
              className={`form-control ${errors.due_day_of_month ? 'error' : ''}`}
              placeholder="Day (1-31)"
              min={1}
              max={31}
            />
            {errors.due_day_of_month && <div className="form-error">{errors.due_day_of_month}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Language</label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              className="form-control"
            >
              {languages.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">{initialData ? 'Updated By *' : 'Created By *'}</label>
            <input
              type="text"
              name={initialData ? "contract_updatedby" : "contract_createdby"}
              value={initialData ? contractUpdatedBy : formData.contract_createdby}
              onChange={(e) => {
                if (initialData) {
                  setContractUpdatedBy(e.target.value);
                } else {
                  handleChange(e);
                }
              }}
              className={`form-control ${errors.contract_createdby ? 'error' : ''}`}
              placeholder="Enter name"
              disabled={!initialData && formData.contract_createdby === 'current_user'}
            />
            {errors.contract_createdby && <div className="form-error">{errors.contract_createdby}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Status</label>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <label className="form-label" style={{ fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  name="isactive"
                  checked={formData.isactive}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Active
              </label>
              <label className="form-label" style={{ fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  name="iscurrent"
                  checked={isCurrent}
                  onChange={(e) => setIsCurrent(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Current
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            name="is_current_pricing_header"
            checked={formData.is_current_pricing_header}
            onChange={handleChange}
            style={{ marginRight: '8px' }}
          />
          Use Current Pricing Header
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default ContractForm;
